import { useState } from 'react';
import { Download, Eye, EyeOff, Trash2 } from 'lucide-react';
import { Page } from '@/components/Page';
import { MedicalDisclaimer } from '@/components/MedicalDisclaimer';
import { Field, inputClass, OptionGroup } from '@/components/ui/Field';
import { PROTEIN_PER_KG } from '@/engine/config';
import { profileProblems } from '@/engine/profile';
import type { ActivityLevel, ManualTargets } from '@/engine/types';
import { deleteAccount } from '@/features/export/deleteAccount';
import { LlmSettings } from '@/features/llm/LlmSettings';
import {
  exportDaysCsv,
  exportFoodsCsv,
  exportJson,
  exportMealsCsv,
  exportWeightsCsv,
  exportWorkoutsCsv,
} from '@/features/export/exportData';
import { saveProfile, useProfileState } from '@/features/profile/useProfile';
import { displayToKg, kgToDisplay, num, todayIso } from '@/lib/format';
import { parseList } from '@/lib/preferences';
import { isSupabaseConfigured } from '@/lib/supabase';
import { useTheme, type ThemePref } from '@/lib/useTheme';
import { cn } from '@/lib/utils';

const THEMES: { value: ThemePref; label: string }[] = [
  { value: 'sistema', label: 'Automático' },
  { value: 'claro', label: 'Claro' },
  { value: 'oscuro', label: 'Oscuro' },
];

const ACTIVITY: { value: ActivityLevel; label: string }[] = [
  { value: 'sedentario', label: 'Sedentario' },
  { value: 'ligero', label: 'Ligero' },
  { value: 'moderado', label: 'Moderado' },
  { value: 'alto', label: 'Alto' },
  { value: 'muy_alto', label: 'Muy alto' },
];

export default function ProfilePage() {
  const { pref, setPref } = useTheme();
  const state = useProfileState();
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  if (state.status !== 'listo') {
    return (
      <Page title="Perfil">
        <p className="text-sm text-[var(--color-text-muted)]">Cargando…</p>
      </Page>
    );
  }

  const { profile, targets } = state;
  const unit = profile.displayUnit;
  const manual = profile.manualTargets ?? {};

  // Un campo vaciado o imposible no se guarda: se dice por qué y se conserva el
  // último valor bueno. Antes un peso vacío guardaba 0 y la app se quedaba en
  // blanco para siempre.
  function save(next: typeof profile) {
    const problems = profileProblems(next, todayIso());
    if (problems.length > 0) {
      setSaveError(problems.join(' '));
      return;
    }
    setSaveError(null);
    saveProfile(next).catch((e: unknown) =>
      setSaveError(e instanceof Error ? e.message : 'No se ha podido guardar.'),
    );
  }

  function patch(changes: Partial<typeof profile>) {
    save({ ...profile, ...changes });
  }

  function patchManual(changes: ManualTargets) {
    save({ ...profile, manualTargets: { ...manual, ...changes } });
  }

  return (
    <Page title="Perfil" subtitle="Objetivos, preferencias y datos">
      <div className="flex flex-col gap-6">
        <section aria-labelledby="objetivos" className="flex flex-col gap-3">
          <h2 id="objetivos" className="text-sm font-semibold">
            Objetivos
          </h2>

          {saveError ? (
            <p
              role="alert"
              className="rounded-xl border border-[var(--color-warn)] bg-[var(--color-warn-soft)] p-3 text-sm"
            >
              No se ha guardado: {saveError}
            </p>
          ) : null}

          <div className="grid grid-cols-2 gap-3">
            <Field label={`Peso (${unit})`}>
              {(id) => (
                <input
                  id={id}
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  defaultValue={round1(kgToDisplay(profile.weightKg, unit))}
                  onBlur={(e) => patch({ weightKg: displayToKg(Number(e.target.value), unit) })}
                  className={cn(inputClass, 'text-right tabular')}
                />
              )}
            </Field>
            <Field label="Altura (cm)">
              {(id) => (
                <input
                  id={id}
                  type="number"
                  inputMode="numeric"
                  defaultValue={profile.heightCm}
                  onBlur={(e) => patch({ heightCm: Number(e.target.value) })}
                  className={cn(inputClass, 'text-right tabular')}
                />
              )}
            </Field>
          </div>

          <OptionGroup
            label="Nivel de actividad"
            value={profile.activity}
            options={ACTIVITY}
            onChange={(activity) => patch({ activity })}
            columns={3}
          />

          <OptionGroup
            label="Objetivo"
            value={profile.goal}
            options={[
              { value: 'perder', label: 'Perder' },
              { value: 'mantener', label: 'Mantener' },
              { value: 'ganar', label: 'Ganar' },
            ]}
            onChange={(goal) => patch({ goal })}
            columns={3}
          />

          {profile.goal !== 'mantener' ? (
            <OptionGroup
              label="Ritmo"
              value={profile.pace}
              options={[
                { value: 'suave', label: 'Suave' },
                { value: 'normal', label: 'Normal' },
                { value: 'agresivo', label: 'Rápido' },
              ]}
              onChange={(pace) => patch({ pace })}
              columns={3}
            />
          ) : null}

          <Field
            label={`Proteína (g/kg): ${num(profile.proteinPerKg, 1)}`}
            hint={`Entre ${num(PROTEIN_PER_KG.min, 1)} y ${num(PROTEIN_PER_KG.max, 1)}.`}
          >
            {(id) => (
              <input
                id={id}
                type="range"
                min={PROTEIN_PER_KG.min}
                max={PROTEIN_PER_KG.max}
                step={0.1}
                value={profile.proteinPerKg}
                onChange={(e) => patch({ proteinPerKg: Number(e.target.value) })}
                className="tap w-full"
              />
            )}
          </Field>
        </section>

        <section aria-labelledby="manual" className="flex flex-col gap-3">
          <h2 id="manual" className="text-sm font-semibold">
            Ajustar a mano
          </h2>
          <p className="-mt-2 text-xs text-[var(--color-text-muted)]">
            Deja en blanco lo que quieras que se siga calculando solo. Las calorías nunca bajan de
            tu metabolismo basal ({num(targets.bmr)} kcal).
          </p>

          <div className="grid grid-cols-2 gap-3">
            <ManualField
              label="Calorías"
              placeholder={num(targets.kcal)}
              value={manual.kcal}
              onChange={(kcal) => patchManual({ kcal })}
            />
            <ManualField
              label="Proteína (g)"
              placeholder={num(targets.proteinG)}
              value={manual.proteinG}
              onChange={(proteinG) => patchManual({ proteinG })}
            />
            <ManualField
              label="Grasas (g)"
              placeholder={num(targets.fatG)}
              value={manual.fatG}
              onChange={(fatG) => patchManual({ fatG })}
            />
            <ManualField
              label="Carbohidratos (g)"
              placeholder={num(targets.carbsG)}
              value={manual.carbsG}
              onChange={(carbsG) => patchManual({ carbsG })}
            />
          </div>
        </section>

        <section aria-labelledby="alimentacion" className="flex flex-col gap-3">
          <h2 id="alimentacion" className="text-sm font-semibold">
            Alimentación
          </h2>
          <p className="-mt-2 text-xs text-[var(--color-text-muted)]">
            Lo que pongas aquí nunca se te propondrá para cerrar el día.
          </p>

          <OptionGroup
            label="¿Eres vegetariano?"
            value={profile.vegetarian ? 'si' : 'no'}
            options={[
              { value: 'no', label: 'No' },
              { value: 'si', label: 'Sí' },
            ]}
            onChange={(v) => patch({ vegetarian: v === 'si' })}
          />

          <Field label="Alergias" hint="Separadas por comas: marisco, frutos secos, lácteos…">
            {(id) => (
              <input
                id={id}
                type="text"
                autoComplete="off"
                defaultValue={profile.allergies.join(', ')}
                onBlur={(e) => patch({ allergies: parseList(e.target.value) })}
                className={inputClass}
              />
            )}
          </Field>

          <Field label="Alimentos que no comes" hint="Separados por comas: coliflor, hígado…">
            {(id) => (
              <input
                id={id}
                type="text"
                autoComplete="off"
                defaultValue={profile.dislikes.join(', ')}
                onBlur={(e) => patch({ dislikes: parseList(e.target.value) })}
                className={inputClass}
              />
            )}
          </Field>
        </section>

        <section aria-labelledby="preferencias" className="flex flex-col gap-3">
          <h2 id="preferencias" className="text-sm font-semibold">
            Preferencias
          </h2>

          <OptionGroup
            label="Unidades"
            value={profile.displayUnit}
            options={[
              { value: 'kg' as const, label: 'Kilos' },
              { value: 'lbs' as const, label: 'Libras' },
            ]}
            onChange={(displayUnit) => patch({ displayUnit })}
          />

          <button
            type="button"
            role="switch"
            aria-checked={profile.hideCalories}
            onClick={() => patch({ hideCalories: !profile.hideCalories })}
            className={cn(
              'tap flex items-center justify-between gap-2 rounded-xl border px-3 text-sm font-medium',
              profile.hideCalories
                ? 'border-[var(--color-brand)] bg-[var(--color-brand-soft)] text-[var(--color-brand-strong)]'
                : 'border-[var(--color-border)]',
            )}
          >
            <span className="text-left">
              Ocultar calorías
              <span className="block text-xs font-normal text-[var(--color-text-muted)]">
                Sigue registrando todo, pero sin ver el número.
              </span>
            </span>
            {profile.hideCalories ? (
              <EyeOff aria-hidden="true" className="size-5 shrink-0" />
            ) : (
              <Eye aria-hidden="true" className="size-5 shrink-0" />
            )}
          </button>

          <OptionGroup
            label="Apariencia"
            value={pref}
            options={THEMES}
            onChange={setPref}
            columns={3}
          />

          <ReminderPicker
            id="recordatorio-dia"
            label="Recordarme marcar el día completo"
            value={profile.reminders?.dayCompleteHour ?? null}
            onChange={(dayCompleteHour) =>
              patch({ reminders: { ...(profile.reminders ?? {}), dayCompleteHour } })
            }
          />

          <ReminderPicker
            id="recordatorio-cierre"
            label="Abrir «Cerrar el día» a esta hora"
            value={profile.reminders?.closeDayHour ?? null}
            onChange={(closeDayHour) =>
              patch({ reminders: { ...(profile.reminders ?? {}), closeDayHour } })
            }
          />

          <p className="-mt-1 text-xs text-[var(--color-text-muted)]">
            Los avisos salen al abrir la app, no como notificación del móvil: mandarlas con la app
            cerrada necesitaría un servidor de envío, y esta app no tiene ninguno.
          </p>
        </section>

        <LlmSettings profile={profile} />

        <section aria-labelledby="datos" className="flex flex-col gap-2">
          <h2 id="datos" className="text-sm font-semibold">
            Tus datos
          </h2>
          <p className="-mt-1 text-xs text-[var(--color-text-muted)]">
            {isSupabaseConfigured
              ? 'Guardados en este dispositivo y en un servidor de la Unión Europea.'
              : 'Guardados solo en este dispositivo. Ver SETUP.md para sincronizar.'}
          </p>

          <div className="grid grid-cols-2 gap-2">
            <ExportButton label="JSON completo" onClick={exportJson} />
            <ExportButton label="Comidas (CSV)" onClick={exportMealsCsv} />
            <ExportButton label="Entrenos (CSV)" onClick={exportWorkoutsCsv} />
            <ExportButton label="Peso (CSV)" onClick={exportWeightsCsv} />
            <ExportButton label="Días (CSV)" onClick={exportDaysCsv} />
            <ExportButton label="Alimentos propios (CSV)" onClick={exportFoodsCsv} />
          </div>
        </section>

        <section aria-labelledby="borrar" className="flex flex-col gap-2">
          <h2 id="borrar" className="text-sm font-semibold text-[var(--color-danger)]">
            Borrar la cuenta
          </h2>
          <p className="text-xs text-[var(--color-text-muted)]">
            Borra todos tus datos, aquí y en el servidor. No se puede deshacer, así que exporta
            antes lo que quieras conservar.
          </p>

          {deleting ? (
            <div className="flex flex-col gap-2 rounded-xl border border-[var(--color-danger)] p-3">
              <label htmlFor="confirmar" className="text-sm">
                Escribe <strong>BORRAR</strong> para confirmar.
              </label>
              <input
                id="confirmar"
                type="text"
                autoComplete="off"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className={inputClass}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setDeleting(false);
                    setConfirmText('');
                  }}
                  className="tap flex-1 rounded-xl border border-[var(--color-border)] px-4 text-sm font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={confirmText.trim().toUpperCase() !== 'BORRAR' || deleteBusy}
                  onClick={() => {
                    setDeleteBusy(true);
                    setDeleteError(null);
                    void deleteAccount().then((result) => {
                      // Solo se sale si de verdad se borró; si no, se dice y no se
                      // toca nada (antes se recargaba igual, como si todo fuera bien).
                      if (result.status === 'borrado') window.location.reload();
                      else {
                        setDeleteError(result.message);
                        setDeleteBusy(false);
                      }
                    });
                  }}
                  className="tap flex-1 rounded-xl bg-[var(--color-danger)] px-4 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {deleteBusy ? 'Borrando…' : 'Borrar todo'}
                </button>
              </div>
              {deleteError ? (
                <p role="alert" className="text-sm text-[var(--color-danger)]">
                  {deleteError}
                </p>
              ) : null}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setDeleting(true)}
              className="tap flex items-center justify-center gap-2 rounded-xl border border-[var(--color-danger)] px-4 text-sm font-medium text-[var(--color-danger)]"
            >
              <Trash2 aria-hidden="true" className="size-4" />
              Borrar mi cuenta y mis datos
            </button>
          )}
        </section>

        <section aria-labelledby="acerca">
          <h2 id="acerca" className="mb-2 text-sm font-semibold">
            Acerca de
          </h2>
          <div className="flex flex-col gap-2 text-xs text-[var(--color-text-muted)]">
            <p>
              Alimentos genéricos:{' '}
              <a
                className="underline"
                href="https://fdc.nal.usda.gov"
                target="_blank"
                rel="noreferrer noopener"
              >
                USDA FoodData Central
              </a>{' '}
              (dominio público, CC0).
            </p>
            <p>
              Productos envasados:{' '}
              <a
                className="underline"
                href="https://es.openfoodfacts.org"
                target="_blank"
                rel="noreferrer noopener"
              >
                Open Food Facts
              </a>
              , bajo{' '}
              <a
                className="underline"
                href="https://opendatacommons.org/licenses/odbl/1-0/"
                target="_blank"
                rel="noreferrer noopener"
              >
                Open Database License (ODbL)
              </a>
              .
            </p>
            <p>Valores de referencia: EFSA Dietary Reference Values (2017).</p>
          </div>
        </section>

        <MedicalDisclaimer />
      </div>
    </Page>
  );
}

function ManualField({
  label,
  placeholder,
  value,
  onChange,
}: {
  label: string;
  placeholder: string;
  value: number | null | undefined;
  onChange: (value: number | null) => void;
}) {
  return (
    <Field label={label}>
      {(id) => (
        <input
          id={id}
          type="number"
          inputMode="numeric"
          min={0}
          placeholder={placeholder}
          defaultValue={value ?? ''}
          onBlur={(e) => {
            const raw = e.target.value.trim();
            onChange(raw === '' ? null : Number(raw));
          }}
          className={cn(inputClass, 'text-right tabular')}
        />
      )}
    </Field>
  );
}

function ExportButton({ label, onClick }: { label: string; onClick: () => Promise<void> }) {
  return (
    <button
      type="button"
      onClick={() => void onClick()}
      className="tap flex items-center justify-center gap-1.5 rounded-xl border border-[var(--color-border)] px-2 text-xs font-medium"
    >
      <Download aria-hidden="true" className="size-4" />
      {label}
    </button>
  );
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * Hora de un recordatorio, o ninguna. Un `select` y no un reloj: son 24
 * opciones y el selector nativo ya lo resuelve en cualquier móvil.
 */
function ReminderPicker({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: number | null;
  onChange: (hour: number | null) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <select
        id={id}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        className="tap rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-2)] px-3 text-sm"
      >
        <option value="">Nunca</option>
        {Array.from({ length: 24 }, (_, hour) => (
          <option key={hour} value={hour}>
            {String(hour).padStart(2, '0')}:00
          </option>
        ))}
      </select>
    </div>
  );
}
